import { Capacitor } from '@capacitor/core';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';

import type { BusinessV3Item } from 'src/sections/admin-page/ListView/Businesses/types';

import DexieService from './DexieService';
import SQLiteService from './SQLiteService';
import { InventoryStats, SystemConfigType } from './types';

class PlatformService {
  private static instance: PlatformService;

  private service: SQLiteService | DexieService;

  private static initPromise: Promise<PlatformService> | null = null;

  // Private constructor now accepts an initialized service instance
  private constructor(service: SQLiteService | DexieService) {
    this.service = service;
  }

  /**
   * Returns the singleton PlatformService after ensuring the database is initialized.
   */
  static async getInstance(): Promise<PlatformService> {
    // 1️⃣ A ready instance? return it.
    if (PlatformService.instance) return PlatformService.instance;

    // 2️⃣ Someone else is already initialising? wait for them.
    if (PlatformService.initPromise) return PlatformService.initPromise;

    // 3️⃣ We are the first – start init and save the promise.
    PlatformService.initPromise = (async () => {
      try {
        const platform = Capacitor.getPlatform();
        let svc: SQLiteService | DexieService;

        if (platform === 'ios' || platform === 'android') {
          const sqliteConn = new SQLiteConnection(CapacitorSQLite);
          const name = 'mydb';

          // Keep native map sane
          const ok = (await sqliteConn.checkConnectionsConsistency()).result;
          if (!ok) await sqliteConn.closeAllConnections();

          const has = (await sqliteConn.isConnection(name, false)).result;
          const db = has
            ? await sqliteConn.retrieveConnection(name, false)
            : await sqliteConn.createConnection(name, false, 'no-encryption', 1, false);

          await db.open();

          svc = new SQLiteService(sqliteConn, db);
        } else if (platform === 'web') {
          // DexieService manages its own Dexie instance internally (v1 + v2 schema).
          svc = new DexieService();
          await svc.initializeDB();
        } else {
          throw new Error('Unsupported platform');
        }

        PlatformService.instance = new PlatformService(svc);
        return PlatformService.instance;
      } catch (error) {
        // CRITICAL FIX: Clear failed promise so retry is possible
        PlatformService.initPromise = null;
        console.error('PlatformService initialization failed:', error);
        throw error;
      }
    })();

    const instance = await PlatformService.initPromise;
    // Clear promise only on success (after await completes)
    PlatformService.initPromise = null;
    return instance;
  }

  // ---------------------------------------------------------------------------
  // system_config delegation
  // ---------------------------------------------------------------------------

  public async closeConnection(): Promise<void> {
    await this.service.closeConnection();
  }

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    await this.service.updateOrCreateSystemConfig(type, data);
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    return this.service.getSystemConfig<T>(type);
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    return this.service.getAllSystemConfigs();
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    await this.service.deleteSystemConfig(type);
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    await this.service.deleteAllSystemConfigs();
  }

  // ---------------------------------------------------------------------------
  // inventory table delegation
  // ---------------------------------------------------------------------------

  public async ensureInventoryTable(): Promise<void> {
    await this.service.ensureInventoryTable();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    await this.service.upsertInventoryItem(product);
  }

  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    await this.service.bulkUpsertInventory(products);
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    await this.service.hardDeleteInventoryItem(productId);
  }

  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
  }): Promise<{ items: ProductDataType[]; total: number }> {
    return this.service.queryInventoryPage(params);
  }

  public async countInventory(): Promise<number> {
    return this.service.countInventory();
  }

  public async sumInventoryStock(): Promise<number> {
    return this.service.sumInventoryStock();
  }

  public async clearAllInventory(): Promise<void> {
    await this.service.clearAllInventory();
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    return this.service.getInventoryById(productId);
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    return this.service.getInventoryByIdentifier(identifier);
  }

  public async getInventoryByVariablePriceLookupCode(lookupCode: string): Promise<ProductDataType | null> {
    return this.service.getInventoryByVariablePriceLookupCode(lookupCode);
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats delegation
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(): Promise<Array<{ category: string; count: number }>> {
    return this.service.getInventoryCategoryCounts();
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    return this.service.recalculateInventoryStats();
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    return this.service.getInventoryStats(options);
  }

  // ---------------------------------------------------------------------------
  // admin_businesses table delegation
  // ---------------------------------------------------------------------------

  public async bulkReplaceAdminBusinesses(businesses: BusinessV3Item[]): Promise<void> {
    await this.service.bulkReplaceAdminBusinesses(businesses);
  }

  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    return this.service.getAllAdminBusinesses();
  }

  public async clearAdminBusinesses(): Promise<void> {
    await this.service.clearAdminBusinesses();
  }
}

export default PlatformService;
